import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActualityAggregatedStatsSortMode } from '../../feed/store/actuality.kpi.types';
import * as Icon from '../icons';
import DropdownSelector from './DropdownSelector';

export type SortOption = {
  key: string;
  title: string;
  icon?: React.ReactNode;
  status?: 'active' | 'inactive';
  onClick: () => void;
};

export type SortDirectionOption = {
  label: string;
  value: 'asc' | 'desc';
  icon: React.ComponentType<any>;
};

type SortMode = ActualityAggregatedStatsSortMode;

export type UnifiedSortOption = {
  key: string;
  mode: SortMode;
  direction: 'asc' | 'desc';
  label: string;
  icon: React.ComponentType<any>;
};

export type SortOptionsSelectorProps = {
  // Sort direction props
  sortDirection: 'asc' | 'desc';
  onChange: (v: { mode: SortMode; direction: 'asc' | 'desc' }) => void;
  onSortDirectionChange?: (direction: 'asc' | 'desc') => void;
  onSortModeChange?: (mode: SortMode) => void; // Sort mode props (group vs metric)
  sortMode?: SortMode;
  // Sort metric props
  sortMetric?: string;
  onSortMetricChange?: (metric: string) => void;
  sortMetricOptions?: Record<string, SortOption>;

  // Group sorting props
  groupTitle?: string;
  groupSortLabel?: string;

  // UI customization
  className?: string;
  showSortDirection?: boolean;
  showSortMetric?: boolean;
  showGroupSort?: boolean;
  showSortModeToggle?: boolean;
  showUnifiedSort?: boolean; // NEW: Show unified sort dropdown
  compact?: boolean;
  panelMode?: boolean;
  vertical?: boolean;
  DropAlignRight?: boolean;
  w_lg?: boolean;
  w_xl?: boolean;

  // Custom sort direction options
  customSortDirectionOptions?: SortDirectionOption[];

  // Labels
  sortDirectionLabel?: string;
  sortMetricLabel?: string;

  // Panel identifier for separate states
  panelId?: string;
};

const SortOptionsSelector: React.FC<SortOptionsSelectorProps> = ({
  sortMode,
  sortDirection,
  onChange,
  className = 'shrink-0 text-xs',
  vertical = false,
  DropAlignRight = false,
  w_lg = false,
  w_xl = false,
  customSortDirectionOptions,
}) => {
  const { t } = useTranslation('feed');

  const unifiedSortOptions: UnifiedSortOption[] = [
    {
      key: 'groupTitle-asc',
      mode: 'groupTitle',
      direction: 'asc',
      label: t('Sort by Group Title... A-Z (ASC)'),
      icon: Icon.SortAscending,
    },
    {
      key: 'groupTitle-desc',
      mode: 'groupTitle',
      direction: 'desc',
      label: t('Sort by Group Title... Z-A (DESC)'),
      icon: Icon.SortDescending,
    },
    {
      key: 'metricValue-asc',
      mode: 'metricValue',
      direction: 'asc',
      label: t('Sort by Metric Value (Lowest First (ASC))'),
      icon: Icon.SortAscending,
    },
    {
      key: 'metricValue-desc',
      mode: 'metricValue',
      direction: 'desc',
      label: t('Sort by Metric Value (Highest First (DESC))'),
      icon: Icon.SortDescending,
    },
    {
      key: 'minTime-asc',
      mode: 'minTime',
      direction: 'asc',
      label: t('Time (Lowest First (ASC))'),
      icon: Icon.SortAscending,
    },
    {
      key: 'minTime-desc',
      mode: 'minTime',
      direction: 'desc',
      label: t('Time (Highest First (DESC))'),
      icon: Icon.SortDescending,
    },
  ];

  // Get current unified sort option
  const currentUnifiedSortKey = `${sortMode}-${sortDirection}`;
  const currentUnifiedOption = unifiedSortOptions.find(
    opt => opt.key === currentUnifiedSortKey,
  );

  // Create dropdown options for unified sort
  const unifiedSortDropdownOptions = _.keyBy(
    unifiedSortOptions.map(opt => ({
      key: opt.key,
      title: opt.label,
      icon: <opt.icon className="h-4 w-4 fill-current" />,
      status: opt.key === currentUnifiedSortKey ? 'active' : 'inactive',
      onClick: () => {
        onChange(opt);
      },
    })),
    'key',
  );
  const currentSortDirectionIcon =
    sortDirection === 'asc' ? Icon.SortAscending : Icon.SortDescending;
  return (
    <DropdownSelector
      className={classNames(className)}
      classNameItem="text-xxs"
      value={currentUnifiedSortKey}
      values={_.keys(unifiedSortDropdownOptions)}
      renderValue={(key: string) => {
        const option = unifiedSortDropdownOptions[key as string];
        if (!option) return null;
        return (
          <div
            className={classNames('flex items-center', {
              // 'text-menu-active-text !group-hover:text-menu-active-text': option.status === 'active',
            })}
          >
            <div className="flex items-center">
              {option.icon}
              <span className="mx-2">{option.title}</span>
            </div>
            {option.status === 'active' && (
              <Icon.CircleOk className="h-4 w-4 fill-current" />
            )}
          </div>
        );
      }}
      renderValueSelected={() => {
        const currentOption = currentUnifiedOption;
        return (
          <div className="flex items-center">
            {currentOption && (
              <currentOption.icon className="h-4 w-4 fill-current" />
            )}
            <span className="ml-1">{currentOption?.label || t('Sort')}</span>
          </div>
        );
      }}
      onChange={(key: string) => {
        const option = unifiedSortDropdownOptions[key as string];
        if (option && _.isFunction(option.onClick)) {
          option.onClick();
        }
      }}
      vertical={vertical}
      panelMode
      w_xl={w_xl}
      w_lg={w_lg}
      DropAlignRight={DropAlignRight}
      iconDropMenu={currentSortDirectionIcon}
      iconSelectionClass={classNames(
        'w-5 sm:w-6 h-5 sm:h-6 xl:w-7 xl:h-7',
        'bg-menu rounded',
      )}
      iconClass={classNames('w-4 h-4 xl:w-5 xl:h-5', 'bg-menu rounded')}
    />
  );
};

export default SortOptionsSelector;
